import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { SettingKey } from "src/shared/api-types/user";

import Spinner from "src/components/Spinner";
import { PERMISSIONS } from "src/constants";
import { useAuthorization } from "src/components/Authorization";
import Hint from "src/components/Hint";
import useFeatureFlag, { FEATURE_FLAGS } from "src/hooks/useFeatureFlag";
import { captureHandledException } from "src/services/sentry";

import ValidatedActionButton, {
  showError,
  showWarning,
  validationPassed,
} from "../../components/ValidatedActionButton";
import { isValidIntegerId } from "../../utils/isValidId";
import { AutocompleteMulti } from "../../components/Autocomplete";
import { Row, StyledPaper, PageHeader } from "../../components/Base";
import TableComponent, { TableHeader } from "../../components/TableComponent";
import { getCrewAircraftTypes, assignAircraftToUser, unassignAircraftFromUser, AircraftType } from "../../api/aircraft";
import AircraftOperationsSection from "./aircraft-operations";
import aircraftOperationCopyImg from "../../assets/images/aircraft-x-operation-copy.png";

interface UserAircraftManagementProps {
  match: {
    params: {
      crewId: string;
      userId: string;
    };
  };
}

interface UseAircraftDataReturn {
  availableAircraft: AircraftType[];
  assignedAircraft: AircraftType[];
  isLoading: boolean;
  refreshData: () => Promise<void>;
}

interface AssignedAircraftTableProps {
  assignedAircraft: AircraftType[];
  canEdit: boolean;
  onUnassign?: (typeId: number) => void;
}

const AssignedAircraftTable: React.FC<AssignedAircraftTableProps> = ({ assignedAircraft, canEdit, onUnassign }) => {
  const { t } = useTranslation();

  const tableHeaders = useMemo(
    (): TableHeader[] => [
      { title: t("aircraft_management.aircraft_type"), style: { width: canEdit ? "45%" : "50%" }, sort: "typeName" },
      {
        title: t("aircraft_management.aircraft_designator"),
        style: { width: canEdit ? "45%" : "50%" },
        sort: "designatorName",
      },
      ...(canEdit ? [{ title: t("actions"), style: { width: "10%" } }] : []),
    ],
    [canEdit, t],
  );

  const renderAircraftRow = useCallback(
    (aircraft: AircraftType): React.JSX.Element => (
      <TableRow key={aircraft.typeId}>
        <TableCell>{aircraft.typeName}</TableCell>
        <TableCell>{aircraft.designatorName ?? "-"}</TableCell>
        {canEdit && (
          <TableCell>
            <IconButton
              size="small"
              title={t("aircraft_management.unassign_aircraft_type")}
              aria-label={String(t("aircraft_management.unassign_aircraft_type"))}
              onClick={() => onUnassign?.(aircraft.typeId)}
            >
              <DeleteForeverIcon color="error" fontSize="small" />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    ),
    [canEdit, onUnassign, t],
  );

  if (assignedAircraft.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <PageHeader>{t("compliance.no_aircraft_assigned")}</PageHeader>
      </Box>
    );
  }

  return (
    <>
      <PageHeader>{t("aircraft_management.assigned_aircraft_types")}</PageHeader>
      <TableComponent header={tableHeaders} renderChildren={renderAircraftRow} items={assignedAircraft} />
    </>
  );
};

const AircraftOperationCopyHint = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const toggleLabel = expanded
    ? t("aircraft_management.collapse_copy_hint")
    : t("aircraft_management.expand_copy_hint");

  return (
    <Hint
      settingKey={SettingKey.AIRCRAFT_OPERATION_COPY_HINT}
      sx={{ my: 2, overflowX: "hidden", "& .MuiAlert-message": { minWidth: 0, width: "100%" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, maxWidth: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <strong>{t("hint_prefix")}:</strong> {t("aircraft_management.copy_hint")}
          </Box>
          <IconButton size="small" onClick={() => setExpanded((value) => !value)} aria-label={toggleLabel}>
            {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ width: "100%", maxWidth: 900, overflowX: "hidden" }}>
            <Box
              component="img"
              src={aircraftOperationCopyImg}
              alt={String(t("aircraft_management.copy_hint_image_alt"))}
              sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                height: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            />
          </Box>
        </Collapse>
      </Box>
    </Hint>
  );
};

const useAircraftData = (crewId: number, userId: number): UseAircraftDataReturn => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [availableAircraft, setAvailableAircraft] = useState<AircraftType[]>([]);
  const [assignedAircraft, setAssignedAircraft] = useState<AircraftType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchAircraftData = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const [userAircraft = [], allAircraft = []] = await Promise.all([
        getCrewAircraftTypes({ crewId, userId }),
        getCrewAircraftTypes({ crewId }),
      ]);

      const userAircraftIds = new Set(userAircraft.map((a) => a.typeId));
      const available = allAircraft.filter((a) => !userAircraftIds.has(a.typeId));

      setAvailableAircraft(available);
      setAssignedAircraft(userAircraft);
    } catch (error) {
      captureHandledException(error, { feature: "userAircraftManagement", action: "loadAircraftData", crewId, userId });
      enqueueSnackbar(t("aircraft_management.load_operations_failed"), { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [crewId, userId, t, enqueueSnackbar]);

  useEffect(() => {
    fetchAircraftData();
  }, [fetchAircraftData]);

  return {
    availableAircraft,
    assignedAircraft,
    isLoading,
    refreshData: fetchAircraftData,
  };
};

const UserAircraftManagement: React.FC<UserAircraftManagementProps> = ({ match }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();
  const aircraftOpsEnabled = useFeatureFlag(FEATURE_FLAGS.AIRCRAFT_OPS);
  const { crewId: crewIdStr, userId: userIdStr } = match.params;
  const crewId = Number(crewIdStr);
  const userId = Number(userIdStr);

  const canEdit = hasPermission(PERMISSIONS.MANAGE_AIRCRAFT_OPERATIONS);

  const [selectedAircraft, setSelectedAircraft] = useState<AircraftType[]>([]);
  const [aircraftSearchTerm, setAircraftSearchTerm] = useState("");
  const [isOperationsLoading, setIsOperationsLoading] = useState(true);

  const { availableAircraft, assignedAircraft, isLoading, refreshData } = useAircraftData(crewId, userId);
  const showAircraftContent = !isLoading && (!aircraftOpsEnabled || !isOperationsLoading);

  const handleAssignAircraft = useCallback(async () => {
    if (selectedAircraft.length === 0) return;

    await Promise.all(
      selectedAircraft.map((aircraft) => assignAircraftToUser({ crewId, typeId: aircraft.typeId, userId })),
    );
    setSelectedAircraft([]);
    refreshData();
  }, [selectedAircraft, userId, crewId, refreshData]);

  const validateAircraftSelection = useCallback(() => {
    if (selectedAircraft.length === 0) {
      return showWarning(t("aircraft_management.select_aircraft_type_first"));
    }

    if (!selectedAircraft.every((aircraft) => isValidIntegerId(aircraft.typeId))) {
      return showError(t("validated_action_button.data_error_refresh"));
    }

    return validationPassed();
  }, [selectedAircraft, t]);

  const handleUnassignAircraft = useCallback(
    async (typeId: number) => {
      await unassignAircraftFromUser({ typeId, userId, crewId });
      refreshData();
    },
    [userId, crewId, refreshData],
  );

  if (isLoading) {
    return <Spinner message={t("loading")} />;
  }

  return (
    <>
      {!showAircraftContent && <Spinner message={t("loading")} />}

      {showAircraftContent && canEdit && (
        <StyledPaper variant="outlined">
          <Row>
            {availableAircraft.length > 0 ? (
              <AutocompleteMulti
                label={t("aircraft_management.select_aircraft_type")}
                options={availableAircraft}
                value={selectedAircraft}
                onChange={setSelectedAircraft}
                getOptionLabel={(a) => a.typeName}
                isOptionEqualToValue={(a, b) => a.typeId === b.typeId}
                disableCloseOnSelect
              />
            ) : (
              <PageHeader>{t("aircraft_management.all_aircraft_types_assigned")}</PageHeader>
            )}
          </Row>

          {availableAircraft.length > 0 && (
            <ValidatedActionButton
              title={t("aircraft_management.assign_aircraft_type")}
              onAction={handleAssignAircraft}
              validate={validateAircraftSelection}
            >
              {t("assign")}
            </ValidatedActionButton>
          )}
        </StyledPaper>
      )}

      {showAircraftContent && aircraftOpsEnabled && canEdit && <AircraftOperationCopyHint />}

      {aircraftOpsEnabled ? (
        <AircraftOperationsSection
          aircraftSearchTerm={aircraftSearchTerm}
          onAircraftSearchChange={setAircraftSearchTerm}
          assignedAircraft={assignedAircraft}
          crewId={crewId}
          userId={userId}
          onUnassign={canEdit ? handleUnassignAircraft : undefined}
          canEdit={canEdit}
          onLoadingChange={setIsOperationsLoading}
          showLoadingSpinner={false}
        />
      ) : (
        <AssignedAircraftTable
          assignedAircraft={assignedAircraft}
          canEdit={canEdit}
          onUnassign={canEdit ? handleUnassignAircraft : undefined}
        />
      )}
    </>
  );
};

export default UserAircraftManagement;
